import { useEffect, useSyncExternalStore } from 'react'
import { getEnvTitlePrefix, subscribeEnvTitlePrefix } from '@/lib/environment-store'

const APP_TITLE = 'Grackle CRM'

/**
 * Sets the browser tab title to "<title> | Grackle CRM" (record name first, so
 * it stays visible when the tab is narrow — like Salesforce). Pass `undefined`
 * while a record is still loading to keep the default. Resets on unmount.
 *
 * OUTSIDE PRODUCTION IT LEADS WITH THE ENVIRONMENT: "[DEV] Opportunities | Grackle CRM".
 * The badge in the header only helps somebody already looking at the page; the tab title is
 * what distinguishes three open tabs, which is where mistaking dev for production actually
 * happens. On production the title is untouched.
 *
 * The prefix comes from a plain store rather than from `useVersion` directly. Every page
 * calls this hook, so subscribing to a query here would require a `QueryClientProvider` in
 * every page test — see `lib/environment-store.ts`.
 *
 * It is applied in BOTH branches. The unmount reset used to set the bare app title, which on
 * any navigation would drop the prefix for as long as the next page took to mount, and a tab
 * that intermittently looks like production is worse than one that never says.
 */
export function useDocumentTitle(title?: string | null) {
  const envPrefix = useSyncExternalStore(
    subscribeEnvTitlePrefix,
    getEnvTitlePrefix,
    getEnvTitlePrefix
  )

  useEffect(() => {
    document.title = title ? `${envPrefix}${title} | ${APP_TITLE}` : `${envPrefix}${APP_TITLE}`
    return () => {
      document.title = `${envPrefix}${APP_TITLE}`
    }
  }, [title, envPrefix])
}
